import React, { useEffect, useMemo, useState } from "react";
import { Card, Table } from "antd";
import type { TableColumnsType } from "antd";

import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import NewInstalledProductModal from "components/new-installed-product-modal/new-installed-product-modal";
import NewProductModal from "components/new-product-modal/new-product-modal";
import MediaPlayer from "components/media-player/media-player";
import StructuredLocation from "components/structured-location/structured-location";

import { DeviceDetectionJobStatus } from "constants/field-values";
import { getFileIcon } from "utils/files-management";
import { usePropertyLocationLabels } from "hooks/usePropertyLocationLabels";

interface HandoverDeviceDetectionResultsProps {
	deviceDetectionCore: any;
	readOnly?: boolean;
}

const HandoverDeviceDetectionResults: React.FC<HandoverDeviceDetectionResultsProps> = ({
	deviceDetectionCore,
	readOnly = false
}) => {
	const {
		addAttachmentModalOpen,
		addAttachmentModalEditMode,
		handleOnCloseAttachmentModal,
		updateAttachments,
		productModalOpen,
		selectedProduct,
		installedProductModalOpen,
		selectedInstallation,
		addInstallModalOpen,
		selectedProductForNewInstall,
		addProductModalOpen,
		selectedProperty,
		fileViewer,
		setFileViewer,
		job,
		handleOpenProductModal,
		handleCloseProductModal,
		handleConfirmProductDeletion,
		handleOpenInstalledProductModal,
		handleCloseInstalledProductModal,
		handleConfirmInstalledProductDeletion,
		handleOpenAddInstallModal,
		handleCloseAddInstallModal,
		addInstalledProduct,
		handleOpenAddProductModal,
		handleCloseAddProductModal,
		addProduct,
		handleOnClickAttachment,
		updateProduct,
		updateInstalledProduct
	} = deviceDetectionCore;

	const { productsGroupedByProductType } = deviceDetectionCore;

	// Draft installations only carry the building/floor/room ids; resolve names from the hierarchy.
	const { getLabels } = usePropertyLocationLabels(selectedProperty?.id ?? job?.property_id);

	// State for expand/collapse
	const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
	const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

	// Initialize all categories and products as expanded
	useEffect(() => {
		if (productsGroupedByProductType && productsGroupedByProductType.length > 0) {
			const categoryStates: Record<string, boolean> = {};
			const productStates: Record<string, boolean> = {};

			productsGroupedByProductType.forEach((category, categoryIndex) => {
				const categoryKey = `${category.product_type?.name}-${categoryIndex}`;
				categoryStates[categoryKey] = true; // Expanded by default

				category.products?.forEach((product: any, productIndex: number) => {
					const productKey = `${categoryKey}-product-${product.draft_product_id || productIndex}`;
					productStates[productKey] = true; // Expanded by default
				});
			});

			setExpandedCategories(categoryStates);
			setExpandedProducts(productStates);
		}
	}, [productsGroupedByProductType]);

	const toggleCategory = (categoryKey: string) => {
		setExpandedCategories(prev => ({
			...prev,
			[categoryKey]: !prev[categoryKey]
		}));
	};

	const toggleProduct = (productKey: string) => {
		setExpandedProducts(prev => ({
			...prev,
			[productKey]: !prev[productKey]
		}));
	};

	// Table columns configuration for installations table
	const installationsTableColumns: TableColumnsType<any> = useMemo(
		() => [
			{
				title: "IMAGE",
				key: "image",
				width: 75,
				render: (_: any, record: any) => {
					let productImgUri = `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`;

					if (record.photo_link) {
						productImgUri = record.photo_link;
					} else {
						if (record.attachments?.length) {
							const attachmentPhoto = record.attachments.find(pa => pa.photo_link)?.photo_link;

							if (attachmentPhoto) {
								productImgUri = attachmentPhoto;
							}
						}
					}

					return (
						<img
							src={productImgUri}
							alt="Installed device icon"
							className={"device-detection-device-header-img" + (record.photo_link ? "" : " placeholder")}
						/>
					);
				}
			},
			{
				title: "LOCATION",
				dataIndex: "location",
				key: "location",
				render: (location: any, record: any) => (
					<>
						<StructuredLocation labels={getLabels(record)} />
						{location || "-"}
					</>
				)
			},
			{
				title: "SERIAL #",
				dataIndex: "product_serial",
				key: "product_serial",
				minWidth: 100,
				render: (serial: any, record: any) => {
					if (!serial || serial === "null" || serial === "not_visible") return "-";
					return serial;
				}
			},
			{
				title: "VISIBLE",
				dataIndex: "installation_visible",
				key: "visible",
				width: 100,
				render: (visible: any) => {
					if (visible) {
						return "Yes";
					} else {
						return "No";
					}
				}
			},
			// Actions column only shown when job is in DRAFT state
			job?.status === DeviceDetectionJobStatus.DRAFT
				? {
						title: "",
						key: "actions",
						minWidth: 100,
						render: (_: any, record: any) => {
							const handleEditClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleOpenInstalledProductModal(record);
							};
							const handleDeleteClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleConfirmInstalledProductDeletion(record);
							};

							if (readOnly) {
								return null;
							}

							return (
								<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className={"selected-file-icon"}
										onClick={handleEditClick}
									/>
									<RemovePopconfirm
										title="Delete Installed Device"
										description={
											<>
												<span>Are you sure you want to delete this installed device?</span>
											</>
										}
										handleDeleteClick={handleDeleteClick}
									/>
								</div>
							);
						}
					}
				: {}
		],
		[job?.status, handleOpenInstalledProductModal, handleConfirmInstalledProductDeletion, readOnly, getLabels]
	);

	if (!productsGroupedByProductType || productsGroupedByProductType.length === 0) {
		return null;
	}

	return (
		<>
			<div className="device-detection-categories-list">
				{productsGroupedByProductType.map((productTypeGroup: any, productTypeIndex: number) => {
					if (productTypeGroup.products.length === 0) return null;

					const categoryKey = `${productTypeGroup.product_type?.name}-${productTypeIndex}`;
					const isCategoryExpanded = expandedCategories[categoryKey] ?? true;

					return (
						<Card
							key={`device-detection-category-${productTypeGroup.product_type?.name}-${productTypeIndex}`}
							className="hdd-summary-card"
						>
							{/* ── Category header with expand/collapse ── */}

							<div className="hdd-section-header">
								<img
									src={
										isCategoryExpanded
											? `${process.env.PUBLIC_URL}/assets/images/icons/chevron-up-gray.svg`
											: `${process.env.PUBLIC_URL}/assets/images/icons/chevron-down-gray.svg`
									}
									alt={isCategoryExpanded ? "collapse" : "expand"}
									className="hdd-expand-icon"
									onClick={() => toggleCategory(categoryKey)}
								/>
								<span className="hdd-section-header-title">
									{`${productTypeGroup.product_type.label} (${productTypeGroup.totalProducts})`}
								</span>
							</div>

							<div className="hdd-expandable">
								<p className="hdd-description">
									{"The following devices were newly installed as part of this project."}
								</p>

								{/* ── Collapsible category content ── */}
								{isCategoryExpanded && (
									<>
										{/* ── Devices in this category ── */}
										{productTypeGroup.products.map((product: any, productIndex: number) => {
											const productImgUri =
												product.photo_link ?? `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`;

											const productKey = `${categoryKey}-product-${product.draft_product_id || productIndex}`;
											const isProductExpanded = expandedProducts[productKey] ?? true;

											return (
												<div key={`product-${product.name}-${productIndex}`} className="hdd-device-container">
													{/* Device info row with expand/collapse */}
													<div className="hdd-device-header-row">
														<div className="hdd-device-header-arrow">
															<img
																src={
																	isProductExpanded
																		? `${process.env.PUBLIC_URL}/assets/images/icons/chevron-up-gray.svg`
																		: `${process.env.PUBLIC_URL}/assets/images/icons/chevron-down-gray.svg`
																}
																alt={isProductExpanded ? "collapse" : "expand"}
																className="hdd-expand-icon"
																onClick={e => {
																	e.stopPropagation();
																	toggleProduct(productKey);
																}}
															/>
														</div>
														<img
															src={productImgUri}
															alt="Device icon"
															className={
																"device-detection-device-header-img" + (product.photo_link ? "" : " placeholder")
															}
														/>
														<div
															className="hdd-device-header-column"
															onClick={() => toggleProduct(productKey)}
															style={{ cursor: "pointer", flex: 1 }}
														>
															<div className="hdd-device-row">
																<span className="hdd-device-name">
																	{`${product.name} (${product.installed_products.length || 0})`}
																</span>
																{product.id && product.id !== "null" ? null : (
																	<span className="hdd-device-name-strong">New</span>
																)}
															</div>

															{product.id && product.id !== "null" && (
																<span className="hdd-device-matched-label">
																	{`Matched product ID:`}
																	<span className="hdd-device-matched-label id">{product.id}</span>
																</span>
															)}
														</div>

														{job?.status === DeviceDetectionJobStatus.DRAFT ? (
															<div className="hdd-device-header-right">
																{product.match_confidence && product.match_confidence !== "no_match" && (
																	<span className={`hdd-confidence ${product.match_confidence}`}>
																		{product.match_confidence} confidence
																	</span>
																)}
																{readOnly ? null : (
																	<>
																		<img
																			alt="edit-attachment-icon"
																			src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
																			className="selected-file-icon"
																			onClick={e => {
																				e.stopPropagation();
																				handleOpenProductModal(product);
																			}}
																		/>
																		<RemovePopconfirm
																			title="Delete Detected Device"
																			description={<span>Are you sure you want to delete this detected device?</span>}
																			handleDeleteClick={() => handleConfirmProductDeletion(product)}
																		/>
																	</>
																)}
															</div>
														) : null}
													</div>

													{product.description && (
														<div className="hdd-body-container">
															<p className="hdd-description">{product.description}</p>
														</div>
													)}

													{/* Collapsible product content */}
													{isProductExpanded && (
														<div className="hdd-body-container">
															{/* Installations table */}
															{product.installed_products && product.installed_products.length > 0 && (
																<div className="hdd-installations-table">
																	<Table
																		dataSource={product.installed_products.map((ip: any) => ({
																			product,
																			...ip
																		}))}
																		columns={installationsTableColumns}
																		pagination={false}
																		rowKey={r => r.draft_installed_product_id || r.id}
																		size="small"
																		expandable={{
																			expandedRowRender: record => (
																				<div className="hdd-installation-expandable">
																					{record.product_notes && (
																						<div>
																							<span className="hdd-device-matched-label">Notes:</span>
																							<p className="hdd-installation-note">{record.product_notes}</p>
																						</div>
																					)}
																					{record.attachments && record.attachments.length > 0 && (
																						<div>
																							<span className="hdd-device-matched-label">
																								Resources ({record.attachments.length}):
																							</span>
																							{record.attachments.map((file: any, index: number) => {
																								const FileIcon = getFileIcon(file.mime_type || "document");
																								return (
																									<div
																										key={`${record.draft_installed_product_id}-${file.name}-${index}`}
																										className="hdd-installation-document"
																										onClick={() => handleOnClickAttachment(file)}
																									>
																										<FileIcon className="hdd-installation-document-icon" />
																										<span className="hdd-installation-document-label">{file.name}</span>
																									</div>
																								);
																							})}
																						</div>
																					)}
																				</div>
																			),
																			rowExpandable: record =>
																				record.product_notes || (record.attachments && record.attachments.length > 0),
																			expandIcon: ({ expanded, onExpand, record }) =>
																				expanded ? (
																					<img
																						src={process.env.PUBLIC_URL + "/assets/images/icons/chevron-up-gray.svg"}
																						alt="chevron up icon"
																						onClick={e => onExpand(record, e)}
																						className="hdd-expand-icon"
																					/>
																				) : (
																					<img
																						src={process.env.PUBLIC_URL + "/assets/images/icons/chevron-down-gray.svg"}
																						alt="chevron down icon"
																						onClick={e => onExpand(record, e)}
																						className="hdd-expand-icon"
																					/>
																				)
																		}}
																	/>
																</div>
															)}
														</div>
													)}

													{/* Add installed device button */}
													{job?.status === DeviceDetectionJobStatus.DRAFT && !readOnly && (
														<div className="hdd-add-install-btn-container">
															<button
																className="hdd-add-install-btn"
																onClick={() => handleOpenAddInstallModal(product)}
															>
																<img
																	src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle-purple.svg`}
																	alt="Add"
																	className="hdd-add-install-btn-icon"
																/>
																Add installed device
															</button>
														</div>
													)}
												</div>
											);
										})}
									</>
								)}
							</div>
						</Card>
					);
				})}
			</div>

			{/* ── Add product button ── */}
			{(job?.status === DeviceDetectionJobStatus.DRAFT || job?.status === DeviceDetectionJobStatus.PUBLISHED) &&
				!readOnly && (
					<div className="hdd-add-product-btn-container">
						<button className="hdd-add-install-btn" onClick={handleOpenAddProductModal}>
							<img
								src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle-purple.svg`}
								alt="Add"
								className="hdd-add-install-btn-icon"
							/>
							Add product
						</button>
					</div>
				)}

			{/* ── Modals ── */}

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
				filesLimit={20}
			/>
			<NewProductModal
				open={productModalOpen}
				onCancel={handleCloseProductModal}
				onSuccess={handleCloseProductModal}
				detectedDevice={selectedProduct}
				onUpdateProduct={updateProduct}
				onClickAttachment={handleOnClickAttachment}
			/>
			<NewProductModal
				open={addProductModalOpen}
				onCancel={handleCloseAddProductModal}
				onSuccess={handleCloseAddProductModal}
				onAddProduct={addProduct}
				onClickAttachment={handleOnClickAttachment}
			/>
			<NewInstalledProductModal
				open={installedProductModalOpen}
				onCancel={handleCloseInstalledProductModal}
				onSuccess={handleCloseInstalledProductModal}
				property={selectedProperty}
				detectedInstallation={selectedInstallation}
				onUpdateInstalledProduct={updateInstalledProduct}
				onClickAttachment={handleOnClickAttachment}
			/>
			<NewInstalledProductModal
				open={addInstallModalOpen}
				onCancel={handleCloseAddInstallModal}
				onSuccess={handleCloseAddInstallModal}
				property={selectedProperty}
				detectedInstallation={
					selectedProductForNewInstall ? ({ product: selectedProductForNewInstall } as any) : undefined
				}
				onAddInstalledProduct={async install => {
					if (selectedProductForNewInstall?.draft_product_id) {
						await addInstalledProduct(selectedProductForNewInstall.draft_product_id, install);
					}
				}}
				onClickAttachment={handleOnClickAttachment}
			/>
			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</>
	);
};

export default HandoverDeviceDetectionResults;
